import type {
  CreateSessionBody,
  ModuleScore,
  Recommendation,
  Session,
  TogethernessMode,
} from '../../lib/types'

/**
 * Presentation-only humanization for the results surface (design-system §5.2:
 * warm, honest, no rubric jargon). The server's explanation/score_breakdown
 * strings carry internal enums (`true_coop`), gate/module names (`quality_gate`)
 * and raw scores (`0.82`); this module maps them to player voice for the CARD.
 * It curates what the card shows — it never invents recommendation logic and
 * never alters the underlying payload (raw data stays on the API surfaces).
 */

// ── Enum → player voice (full TogethernessMode set from lib/types.ts) ──
export const TOGETHERNESS_PHRASES: Record<TogethernessMode, string> = {
  single_player: 'solo play',
  joinable_parallel: 'playing alongside each other',
  true_coop: 'co-op together',
  competitive: 'head-to-head',
  party: 'a party night',
}

const MODE_PATTERN = /(single_player|joinable_parallel|true_coop|competitive|party)/

/**
 * The GLOBAL swap (applied to arbitrary prose) targets only underscore-bearing
 * enum tokens, word-bounded: snake_case can only ever be a leaked enum, while
 * bare "party"/"competitive" are legitimate English in natural prose ("a
 * perfect party game", "uncompetitive") and must never be mangled. Those two
 * still humanize on the anchored paths (REASON_REWRITES / synthesizeWhy),
 * where they provably appear as enums.
 */
const LEAKED_ENUM_PATTERN = /\b(single_player|joinable_parallel|true_coop)\b/g

function phraseFor(mode: string | null | undefined): string | null {
  if (!mode) return null
  return TOGETHERNESS_PHRASES[mode as TogethernessMode] ?? null
}

// ── Gate/module names → plain labels ──
const MODULE_LABELS: Record<string, string> = {
  taste_match: 'Taste match',
  recency: 'Into lately',
  population_prior: 'Crowd signal',
  coverage_k: 'Group fit',
  feed_relevance: 'Why it surfaced',
  novelty_dampener: 'Kept fresh',
  quality_gate: 'Quality check',
  spotlight: 'Spotlight',
  togetherness: 'Plays together',
  togetherness_gate: 'Plays together',
}

export function moduleLabel(module: string | null | undefined): string | null {
  if (!module) return null
  const known = MODULE_LABELS[module]
  if (known) return known
  // Unknown module names still never render as snake_case.
  const words = module.replace(/[_-]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null
}

// ── Raw reason strings → player voice ──
// Rows that only describe internal absence/bookkeeping are dropped (null).
const DROPPED_REASONS = [/^no aggregate signal available$/i, /^contribution from /i]

const REASON_REWRITES: Array<[RegExp, string | ((m: RegExpMatchArray) => string)]> = [
  [/^proven: enough ratings, not confidently poor$/i, 'plenty of ratings, and they hold up'],
  [/^unproven — ranked with low confidence$/i, 'not many reviews yet'],
  [/^aspect profile aligns with the group's tastes$/i, "a strong match for this group's taste"],
  [/^weak aspect alignment$/i, "a stretch for this group's usual taste"],
  [/^not a current-interest match$/i, "not what you've been playing lately"],
  [
    /^(\d+) of (\d+) would highly enjoy this \(need \d+\)$/i,
    (m) => `${m[1]} of ${m[2]} of you would love it`,
  ],
  [/^fits the (\w+) vibe$/i, (m) => `made for ${phraseFor(m[1]) ?? 'tonight'}`],
  [
    /^playable but not a natural (\w+) pick$/i,
    (m) => `works for ${phraseFor(m[1]) ?? 'tonight'}, but not built for it`,
  ],
  [/^supports (\w+) play$/i, (m) => `supports ${phraseFor(m[1]) ?? 'playing together'}`],
  [
    /^the group already plays this a lot — dampened so we surface something fresher$/i,
    'you already play this a lot, so we nudged it down',
  ],
  [/^under-the-radar pick matching ember predicate .*$/i, "an under-the-radar pick with this group's name on it"],
  [/^under-the-radar pick — few reviews yet but a strong fit.*$/i, 'under the radar — few reviews yet, but a strong fit'],
]

/** Strip raw numeric scores like " (0.82)" — never shown to players (§5.2). */
function stripScores(text: string): string {
  return text.replace(/\s*\(\d+(?:\.\d+)?\)/g, '')
}

/** Swap any leaked snake_case mode enums (e.g. `true_coop`) for their player phrases. */
function swapModeEnums(text: string): string {
  return text.replace(LEAKED_ENUM_PATTERN, (mode) => phraseFor(mode) ?? mode)
}

function tidy(text: string): string {
  return text.replace(/\s+/g, ' ').replace(/\s+([,;.])/g, '$1').trim()
}

/**
 * One raw module reason → player voice, or null when the row carries nothing a
 * player can use. Pure string mapping; unknown strings fall through with the
 * generic passes (score strip + enum swap) so jargon never leaks.
 */
export function humanizeReason(reason: string | null | undefined): string | null {
  if (!reason) return null
  const trimmed = tidy(reason)
  if (!trimmed) return null
  if (DROPPED_REASONS.some((re) => re.test(trimmed))) return null
  for (const [pattern, replacement] of REASON_REWRITES) {
    const match = trimmed.match(pattern)
    if (match) return typeof replacement === 'string' ? replacement : replacement(match)
  }
  const generic = tidy(swapModeEnums(stripScores(trimmed)))
  return generic || null
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** The stage-8 fallback appends this to non-primary concatenated whys. */
const EXPLORATION_SUFFIX = /\s*\(offered as an under-the-radar exploration pick\)\s*$/i

export interface ReasonRow {
  label: string | null
  text: string
}

export interface ResultCopy {
  /** The card's one "why this fits" line, or null when nothing usable exists. */
  why: string | null
  /** Top 2–3 humanized evidence rows — never verbatim duplicates of the why. */
  reasons: ReasonRow[]
}

interface ScoredRow {
  module: string
  label: string | null
  text: string
  contribution: number
}

function usableRows(breakdown: ModuleScore[]): ScoredRow[] {
  const rows: ScoredRow[] = []
  for (const m of breakdown) {
    const text = humanizeReason(m.reason)
    if (!text) continue
    rows.push({
      module: m.module ?? '',
      label: moduleLabel(m.module),
      text,
      contribution: m.contribution ?? 0,
    })
  }
  return rows
}

/**
 * Detects the deterministic server why (a "; "-joined concatenation of the
 * pick's own module reasons) so the card can synthesize a plain-language line
 * instead of echoing the rows verbatim.
 */
function isConcatenatedWhy(explanation: string, breakdown: ModuleScore[]): boolean {
  const rawReasons = new Set(
    breakdown.map((m) => m.reason?.trim()).filter((r): r is string => Boolean(r)),
  )
  if (rawReasons.size === 0) return false
  return explanation
    .replace(EXPLORATION_SUFFIX, '')
    .split('; ')
    .some((part) => rawReasons.has(part.trim()))
}

/**
 * Synthesized why for concatenated server explanations: short fragments built
 * from the pick's own grounded signals (togetherness fit, taste, quality),
 * referencing the chosen vibe + roster in player voice — never raw module rows.
 * Returns the sentence plus which modules it consumed (excluded from the rows).
 */
function synthesizeWhy(
  breakdown: ModuleScore[],
  mode: TogethernessMode | null | undefined,
): { why: string | null; usedModules: Set<string> } {
  const used = new Set<string>()
  const fragments: string[] = []
  let hasQualityFragment = false

  const byContribution = [...breakdown].sort(
    (a, b) => (b.contribution ?? 0) - (a.contribution ?? 0),
  )

  for (const m of byContribution) {
    if (fragments.length >= 3) break
    const module = m.module ?? ''
    const reason = m.reason ?? ''
    if ((module === 'togetherness' || module === 'togetherness_gate') && /^(fits the|supports)/i.test(reason)) {
      const phrase = phraseFor(mode) ?? phraseFor(reason.match(MODE_PATTERN)?.[1])
      if (phrase && !used.has('togetherness') && !used.has('togetherness_gate')) {
        fragments.push(`made for ${phrase}`)
        used.add(module)
      }
    } else if (module === 'taste_match' && /aligns/i.test(reason)) {
      fragments.push("right in this group's taste")
      used.add(module)
    } else if (module === 'quality_gate' && /^proven:/i.test(reason) && !hasQualityFragment) {
      fragments.push('well reviewed')
      hasQualityFragment = true
      used.add(module)
    } else if (module === 'feed_relevance' && /highly rated/i.test(reason) && !hasQualityFragment) {
      fragments.push('highly rated')
      hasQualityFragment = true
      used.add(module)
    }
  }

  if (fragments.length === 0) return { why: null, usedModules: used }
  const [first, ...rest] = fragments
  const why =
    rest.length === 0
      ? `${capitalize(first)}.`
      : rest.length === 1
        ? `${capitalize(first)}, and ${rest[0]}.`
        : `${capitalize(first)}, ${rest[0]}, and ${rest[1]}.`
  return { why, usedModules: used }
}

/**
 * The card's copy, curated: ONE why-line + top 2–3 humanized reason rows
 * (§6.2 one why, §1.6 restraint). Handles both server shapes:
 * - a real prose explanation → lightly humanized (scores stripped, enums
 *   swapped) and rendered as-is;
 * - the deterministic concatenation of module reasons → replaced with a
 *   synthesized player-voice line, its source rows excluded from the list.
 */
export function presentResultCopy(
  rec: Pick<Recommendation, 'explanation' | 'score_breakdown'>,
  togethernessMode?: TogethernessMode | null,
): ResultCopy {
  const breakdown = rec.score_breakdown ?? []
  const rows = usableRows(breakdown)
  const explanation = rec.explanation?.trim() || null

  let why: string | null = null
  let usedModules = new Set<string>()

  if (explanation && isConcatenatedWhy(explanation, breakdown)) {
    ;({ why, usedModules } = synthesizeWhy(breakdown, togethernessMode))
  } else if (explanation) {
    why = tidy(swapModeEnums(stripScores(explanation.replace(EXPLORATION_SUFFIX, '')))) || null
  }

  const reasons = rows
    .filter((r) => !usedModules.has(r.module))
    .filter((r) => !why || r.text.toLowerCase() !== why.toLowerCase())
    .sort((a, b) => b.contribution - a.contribution)
    .slice(0, 3)
    .map(({ label, text }) => ({ label, text }))

  return { why, reasons }
}

// ── Steam store categories → human chips (shared: card chips + drawer) ──
const CATEGORY_LABELS: Record<string, string> = {
  single_player: 'Single-player',
  co_op: 'Co-op',
  online_co_op: 'Online co-op',
  pvp: 'PvP',
  mmo: 'MMO',
  shared_split_screen: 'Couch',
}

/** Known store categories map to product labels; unknown ones are prettified. */
export function categoryLabel(category: string): string {
  const known = CATEGORY_LABELS[category]
  if (known) return known
  const words = category.replace(/[_-]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : category
}

/** True when the category has a product label (the card shows only these). */
export function isKnownCategory(category: string): boolean {
  return Boolean(CATEGORY_LABELS[category])
}

// ── Recap fallbacks (deep-linked sessions have no wizard snapshot) ──

/**
 * Roster size for the recap strip: the wizard snapshot when we have it, else
 * the session payload's own roster_readiness.total (the real roster count),
 * else the first pick's per_user rows. Never renders "0 players".
 */
export function recapRosterSize(
  snapshot: Pick<CreateSessionBody, 'roster_user_ids'> | null,
  session: Session | null,
): number {
  const fromSnapshot = snapshot?.roster_user_ids?.length ?? 0
  if (fromSnapshot > 0) return fromSnapshot
  const fromReadiness = session?.roster_readiness?.total ?? 0
  if (fromReadiness > 0) return fromReadiness
  const fromPerUser = session?.recommendations?.[0]?.per_user?.length ?? 0
  if (fromPerUser > 0) return fromPerUser
  return 1
}

const VIBE_LABEL_MAX = 42

/**
 * The chosen vibe for the recap strip (§6.5 "4 players · co-op · chill"):
 * the submitted vibe text when the snapshot holds it, else the session's
 * parsed vibe reweight terms, else null (chip omitted).
 */
export function recapVibeLabel(
  snapshot: Pick<CreateSessionBody, 'vibe_text'> | null,
  session: Session | null,
): string | null {
  const text = snapshot?.vibe_text?.trim()
  if (text) {
    return text.length > VIBE_LABEL_MAX ? `${text.slice(0, VIBE_LABEL_MAX - 1).trimEnd()}…` : text
  }
  // Reweight keys are raw aspect enums (e.g. tone_mood) — de-snake them so a
  // deep-linked recap never leaks rubric jargon (§5.2), same rule as
  // moduleLabel's unknown-name fallback.
  const terms = Object.keys(session?.vibe_constraints?.reweights ?? {}).map((t) =>
    t.replace(/[_-]+/g, ' ').trim(),
  )
  if (terms.length > 0) return terms.slice(0, 3).join(', ')
  return null
}
